import { useEffect, useState } from "react";
import { useAuth } from "@clerk/react";
import { Volume2, Square, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { makeApi } from "@/lib/api";
import { useT } from "@/i18n";

let active = null;
let seq = 0;
export const stopActive = () => { seq += 1; if (active) { active.audio?.pause(); active.onEnd(); active = null; } };

const MSG = {
  es: { limit: "Usaste las 30 voces gratis de hoy. ¡Vuelve mañana o pide el Plan Familia!", fail: "No pude hablar ahora, intenta otra vez", unsafe: "Eso no lo puedo leer en voz alta.", login: "Entra para escuchar a las mascotas", lumi: "Lumi lee", spark: "Spark lee", stop: "Parar" },
  en: { limit: "You used your 30 free voices today. Come back tomorrow or ask for the Family Plan!", fail: "I couldn't speak right now, try again", unsafe: "I can't read that out loud.", login: "Sign in to hear the mascots", lumi: "Lumi reads", spark: "Spark reads", stop: "Stop" },
};

export const playExclusive = (audio, owner = "other") => {
  stopActive();
  active = { audio, onEnd: () => {}, owner };
  audio.onended = () => { if (active?.audio === audio) active = null; };
  return audio.play();
};

export const playClip = (name, lang) =>
  playExclusive(new Audio(`/audio/${name}-${lang === "en" ? "en" : "es"}.mp3?v=7`), name).catch(() => {});

export default function SpeakButton({ mascot = "lumi", text, testId, compact = false }) {
  const { getToken, isSignedIn, isLoaded } = useAuth();
  const { lang } = useT();
  const m = MSG[lang] || MSG.es;
  const [state, setState] = useState("idle");
  const [queued, setQueued] = useState(false);
  useEffect(() => () => { if (active?.owner === testId) stopActive(); }, [testId]);

  const play = async () => {
    if (state !== "idle" && !queued) { stopActive(); setState("idle"); return; }
    if (!isLoaded) { setQueued(true); setState("loading"); return; }
    setQueued(false);
    if (!isSignedIn) { setState("idle"); toast.info(m.login); return; }
    stopActive();
    const my = seq;
    active = { onEnd: () => setState("idle"), owner: testId };
    setState("loading");
    try {
      const r = await makeApi(getToken).post("/voice", { mascot, text: text.slice(0, 2500), lang: lang === "en" ? "en" : "es" }, { responseType: "blob", timeout: 60000 });
      if (my !== seq) return;
      const url = URL.createObjectURL(r.data);
      const audio = new Audio(url);
      const onEnd = () => { setState("idle"); URL.revokeObjectURL(url); };
      audio.onended = () => { active = null; onEnd(); };
      active = { audio, onEnd, owner: testId };
      await audio.play();
      setState("playing");
    } catch (e) {
      if (my !== seq) return;
      active = null;
      setState("idle");
      toast.error(e.response?.status === 402 ? m.limit : e.response?.status === 422 ? m.unsafe : m.fail);
    }
  };

  useEffect(() => { if (queued && isLoaded) play(); }, [queued, isLoaded]); // eslint-disable-line react-hooks/exhaustive-deps


  const color = mascot === "lumi" ? "bg-yellow-300 text-blue-950" : "bg-sky-400 text-white";
  const Icon = state === "loading" ? Loader2 : state === "playing" ? Square : Volume2;
  return (
    <button type="button" onClick={play} className={`glossy-btn ${color} rounded-full font-fredoka font-bold inline-flex shrink-0 items-center gap-2 ${compact ? "px-2.5 py-1 text-xs" : "px-4 py-2 text-sm"}`} data-testid={testId} aria-label={m[mascot]}>
      <img src={`/img/${mascot}.webp`} alt="" className={compact ? "w-5 h-5 object-contain" : "w-7 h-7 object-contain"} />
      <Icon className={`${compact ? "w-3.5 h-3.5" : "w-4 h-4"} ${state === "loading" ? "animate-spin" : ""}`} />
      {!compact && (state === "playing" ? m.stop : m[mascot])}
    </button>
  );
}
